// src/pages/Cliente/MisInscripciones/index.jsx
import React, { useEffect, useState } from "react";
import {
    Card, Button, Space, Typography, Tag,
    Descriptions, Divider, Modal, Row, Col, Statistic
} from "antd";
import {
    TrophyOutlined, CheckCircleOutlined, CalendarOutlined,
    UserOutlined, EnvironmentOutlined, DollarOutlined,
    EyeOutlined, ClockCircleOutlined,
    PhoneOutlined, MailOutlined, QrcodeOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import "dayjs/locale/es";
import Inscripcion from "../../../../service/Inscripciones";
import Pago from "../../../../service/Pago";
import Acceso from "../../../../service/Acceso";
import QRAccesoModal from "../../../Acceso/QRAccesoModal";
import { useNotify } from "../../../../providers/NotificationProvider";
import { Link } from "react-router-dom";
import { FiArrowRight, FiAward, FiUsers, FiChevronRight } from "react-icons/fi";
import EncabezadoPagina from "../../../Ui/Cliente/EncabezadoPagina";
import EstadoVacio, { claseBotonPrimario } from "../../../Ui/Cliente/EstadoVacio";
import TalleresSugeridos from "../../../Ui/Cliente/TalleresSugeridos";

dayjs.locale("es");

const { Title, Text } = Typography;

const DIA_ABBR = {
    lunes: 'Lun', martes: 'Mar', 'miércoles': 'Mié',
    jueves: 'Jue', viernes: 'Vie', 'sábado': 'Sáb', domingo: 'Dom',
};

export default function MisInscripciones() {
    const [loading,               setLoading]               = useState(true);
    const [inscripciones,         setInscripciones]         = useState([]);
    const [selectedInscripcion,   setSelectedInscripcion]   = useState(null);
    const [detalleModalVisible,   setDetalleModalVisible]   = useState(false);
    const [qrModalOpen,           setQrModalOpen]           = useState(false);
    const [qrLoading,             setQrLoading]             = useState(false);
    const [qrData,                setQrData]                = useState(null);
    const [qrError,               setQrError]               = useState(null);

    const notify = useNotify();

    useEffect(() => { loadInscripcionesActivas(); }, []);

    const loadInscripcionesActivas = async () => {
        try {
            setLoading(true);
            const res = await Inscripcion.MisInscripciones({ estado: 'confirmada', vigente: true });
            setInscripciones(res || []);
        } catch (error) {
            console.error('Error:', error);
            notify.error("No se pudieron cargar las inscripciones");
        } finally {
            setLoading(false);
        }
    };

    const handleVerDetalle = (inscripcion) => {
        setSelectedInscripcion(inscripcion);
        setDetalleModalVisible(true);
    };

    const handleGenerarQR = async () => {
        setQrModalOpen(true);
        setQrLoading(true);
        setQrError(null);
        setQrData(null);
        try {
            const data = await Acceso.GenerarQR();
            setQrData(data);
        } catch (error) {
            setQrError(error.message || 'No se pudo generar el QR');
        } finally {
            setQrLoading(false);
        }
    };

    const handleDescargarBoleta = async (inscripcion) => {
        try {
            if (!inscripcion.ordenCompraId) { notify.warning("No hay boleta disponible"); return; }
            if (inscripcion.ordenCompraId?.facturacion?.urlPdf) {
                window.open(inscripcion.ordenCompraId.facturacion.urlPdf, '_blank');
                notify.success("Abriendo boleta...");
                return;
            }
            const pagos = await Pago.MisPagos();
            const pago  = pagos.find(p => p.ordenCompra?._id === inscripcion.ordenCompraId._id);
            if (pago?.facturacion?.urlPdf) {
                window.open(pago.facturacion.urlPdf, '_blank');
                notify.success("Abriendo boleta...");
            } else {
                notify.warning("La boleta aún no está disponible");
            }
        } catch (error) {
            console.error('Error:', error);
            notify.error("No se pudo descargar la boleta");
        }
    };

    const calcularDiasRestantes = (fechaInicio, fechaTermino) => {
        if (!fechaInicio || !fechaTermino) return 'N/A';
        const hoy     = dayjs();
        const inicio  = dayjs(fechaInicio);
        const termino = dayjs(fechaTermino);
        if (hoy.isBefore(inicio))  return termino.diff(inicio, 'day');
        if (hoy.isAfter(termino))  return 0;
        return termino.diff(hoy, 'day');
    };

    const getHorarioInfo = (horario) => {
        if (!horario || !horario.bloques) return 'Horario no especificado';
        return horario.bloques.map(b => `${b.dias?.join(', ')} ${b.inicio}-${b.fin}`).join(' | ');
    };

    const getRecintoInfo = (taller) => {
        if (!taller?.recinto) return 'Recinto no especificado';
        return typeof taller.recinto === 'string' ? taller.recinto : taller.recinto.nombre || 'Recinto sin nombre';
    };

    // ── Helper: render bloques compactos ──────────────────────────────────
    const BloqueChips = ({ bloques }) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {(bloques || []).map((b, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', gap: 3 }}>
                        {(b.dias || []).map(d => (
                            <span key={d} style={{ fontSize: 11, fontWeight: 600, padding: '1px 7px', borderRadius: 99, background: '#eff6ff', color: '#003a94', textTransform: 'capitalize' }}>
                                {DIA_ABBR[d] || d.slice(0, 3)}
                            </span>
                        ))}
                    </div>
                    <span style={{ color: '#d1d5db' }}>|</span>
                    <span style={{ fontSize: 13, color: '#374151', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <ClockCircleOutlined style={{ color: '#0047bb', fontSize: 12 }} />
                        {b.inicio} – {b.fin}
                    </span>
                </div>
            ))}
        </div>
    );

    // ── Helper: contador de días ──────────────────────────────────────────
    const DiasCounter = ({ dias }) => {
        if (dias === 'N/A') return <Text type="secondary">N/A</Text>;
        const color = dias === 0 ? '#ff4d4f' : dias <= 7 ? '#fa8c16' : dias <= 30 ? '#b45309' : '#0047bb';
        return (
            <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 28, fontWeight: 800, color, lineHeight: 1 }}>{dias}</div>
                <Text type="secondary" style={{ fontSize: 11 }}>días restantes</Text>
            </div>
        );
    };

    return (
        <div className="min-h-screen">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-10">

                {/* ── Header ───────────────────────────────────────────────── */}
                <EncabezadoPagina
                    icon={<TrophyOutlined />}
                    titulo="Mis"
                    destacado="talleres"
                    subtitulo="Tus inscripciones activas en talleres municipales"
                >
                    {inscripciones.length > 0 && (
                        <>
                            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/25 text-[#00266b] text-sm font-bold">
                                <span className="text-lg leading-none">{inscripciones.length}</span>
                                activa{inscripciones.length !== 1 ? 's' : ''}
                            </span>
                            <button type="button" onClick={handleGenerarQR} className={claseBotonPrimario}>
                                <QrcodeOutlined /> Generar QR de acceso
                            </button>
                        </>
                    )}
                </EncabezadoPagina>

                {/* ── Loading ───────────────────────────────────────────────── */}
                {loading && (
                    <div className="flex flex-col gap-4" aria-busy="true" aria-label="Cargando inscripciones">
                        {[0, 1].map(i => (
                            <div key={i} className="rounded-2xl bg-white p-6 animate-pulse">
                                <div className="h-5 bg-gray-200 rounded-full w-1/3 mb-3" />
                                <div className="h-3 bg-gray-100 rounded-full w-1/2 mb-6" />
                                <div className="h-3 bg-gray-100 rounded-full w-2/3" />
                            </div>
                        ))}
                    </div>
                )}

                {/* ── Empty ─────────────────────────────────────────────────── */}
                {!loading && inscripciones.length === 0 && (
                    <>
                        <EstadoVacio
                            icon={<TrophyOutlined />}
                            titulo="Aún no tienes talleres activos"
                            texto="Cuando te inscribas en un taller aparecerá aquí, con tus horarios, los días que te quedan y tu QR de acceso."
                            acciones={
                                <Link to="/#talleres-section" className={claseBotonPrimario}>
                                    Explorar talleres <FiArrowRight size={15} />
                                </Link>
                            }
                        >
                            <div className="mt-10 pt-8 border-t border-gray-100 w-full max-w-lg grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {[
                                    { to: '/mis-matriculas', icon: <FiAward size={18} />, titulo: 'Mis matrículas', texto: 'Matrículas de recinto vigentes' },
                                    { to: '/familiares', icon: <FiUsers size={18} />, titulo: 'Mis cargas', texto: '¿Inscribiste a un familiar?' },
                                ].map(a => (
                                    <Link key={a.to} to={a.to}
                                        className="group flex items-center gap-3 p-3.5 rounded-2xl border border-gray-100 bg-white hover:border-secondary hover:bg-[#fffbea] text-left transition-colors">
                                        <span className="w-10 h-10 flex-shrink-0 rounded-xl bg-blue-50 text-primary flex items-center justify-center group-hover:bg-secondary group-hover:text-[#00266b] transition-colors">
                                            {a.icon}
                                        </span>
                                        <span className="min-w-0">
                                            <span className="block text-sm font-bold text-gray-900">{a.titulo}</span>
                                            <span className="block text-xs text-gray-500 truncate">{a.texto}</span>
                                        </span>
                                        <FiChevronRight size={16} className="ml-auto text-gray-300 group-hover:text-[#00266b]" />
                                    </Link>
                                ))}
                            </div>
                        </EstadoVacio>
                        <TalleresSugeridos />
                    </>
                )}

                {/* ── Lista ─────────────────────────────────────────────────── */}
                {!loading && inscripciones.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        {inscripciones.map((ins) => {
                            const dias       = calcularDiasRestantes(ins.duracion?.fechaInicio, ins.duracion?.fechaTermino);
                            const recintoInfo = getRecintoInfo(ins.taller);

                            return (
                                <Card
                                    key={ins._id}
                                    style={{ borderRadius: 16, borderLeft: '5px solid #feda2c' }}
                                    styles={{ body: { padding: '20px 24px' } }}
                                >
                                    {/* Encabezado */}
                                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 16 }}>
                                        <div style={{ flex: 1 }}>
                                            <Title level={4} style={{ margin: '0 0 6px' }}>{ins.taller?.nombre}</Title>
                                            <Space wrap size={4}>
                                                <Tag color="blue" icon={<CheckCircleOutlined />}>Activa</Tag>
                                                <Tag color="purple">{ins.plan?.nombre}</Tag>
                                                <Tag color="default" icon={<EnvironmentOutlined />}>{recintoInfo}</Tag>
                                            </Space>
                                        </div>
                                        <Button
                                            type="primary"
                                            size="small"
                                            icon={<EyeOutlined />}
                                            onClick={() => handleVerDetalle(ins)}
                                            style={{ background: '#0047bb', borderColor: '#0047bb', flexShrink: 0 }}
                                        >
                                            Ver detalle
                                        </Button>
                                    </div>

                                    <Divider style={{ margin: '0 0 16px' }} />

                                    {/* Cuerpo: horarios + datos + contador */}
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 16, alignItems: 'start' }}>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                            {/* Horarios */}
                                            <div>
                                                <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: 6 }}>
                                                    <CalendarOutlined style={{ marginRight: 4 }} />Horario de clases
                                                </Text>
                                                <BloqueChips bloques={ins.horario?.bloques} />
                                            </div>

                                            {/* Meta-info: inscrito, período, monto */}
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 32px' }}>
                                                <div>
                                                    <Text type="secondary" style={{ fontSize: 11 }}>
                                                        <UserOutlined style={{ marginRight: 4 }} />Inscrito
                                                    </Text>
                                                    <Text strong style={{ fontSize: 13, display: 'block' }}>{ins.inscrito?.nombre}</Text>
                                                </div>
                                                <div>
                                                    <Text type="secondary" style={{ fontSize: 11 }}>📅 Período</Text>
                                                    <Text style={{ fontSize: 13, display: 'block' }}>
                                                        {dayjs(ins.duracion?.fechaInicio).format('DD/MM/YY')} – {dayjs(ins.duracion?.fechaTermino).format('DD/MM/YY')}
                                                    </Text>
                                                </div>
                                                <div>
                                                    <Text type="secondary" style={{ fontSize: 11 }}>
                                                        <DollarOutlined style={{ marginRight: 4 }} />Valor
                                                    </Text>
                                                    <Text strong style={{ fontSize: 13, display: 'block', color: '#0047bb' }}>
                                                        ${ins.plan?.valorFinal?.toLocaleString('es-CL')}
                                                    </Text>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Días restantes */}
                                        <div style={{ padding: '10px 16px', background: '#fffbea', borderRadius: 14, border: '1px solid #fde68a', textAlign: 'center', minWidth: 90 }}>
                                            <DiasCounter dias={dias} />
                                        </div>
                                    </div>
                                </Card>
                            );
                        })}
                    </div>
                )}

                {/* ── Modal detalle ─────────────────────────────────────────── */}
                <Modal
                    title={<Space><TrophyOutlined style={{ color: '#0047bb' }} /><span>Detalle de Inscripción</span></Space>}
                    open={detalleModalVisible}
                    onCancel={() => setDetalleModalVisible(false)}
                    footer={<Button type="primary" onClick={() => setDetalleModalVisible(false)} style={{ background: '#0047bb', borderColor: '#0047bb' }}>Cerrar</Button>}
                    width={680}
                    styles={{ body: { padding: '16px 24px' } }}
                >
                    {selectedInscripcion && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                            <Descriptions title="Inscrito" column={1} bordered size="small">
                                <Descriptions.Item label="Nombre">{selectedInscripcion.inscrito?.nombre}</Descriptions.Item>
                                <Descriptions.Item label="RUT">{selectedInscripcion.inscrito?.rut}</Descriptions.Item>
                                {selectedInscripcion.inscrito?.telefono && (
                                    <Descriptions.Item label="Teléfono"><Space><PhoneOutlined />{selectedInscripcion.inscrito.telefono}</Space></Descriptions.Item>
                                )}
                                {selectedInscripcion.inscrito?.email && (
                                    <Descriptions.Item label="Email"><Space><MailOutlined />{selectedInscripcion.inscrito.email}</Space></Descriptions.Item>
                                )}
                            </Descriptions>
                            <Descriptions title="Taller y Horario" column={1} bordered size="small">
                                <Descriptions.Item label="Taller">{selectedInscripcion.taller?.nombre}</Descriptions.Item>
                                <Descriptions.Item label="Recinto">{getRecintoInfo(selectedInscripcion.taller)}</Descriptions.Item>
                                <Descriptions.Item label="Horarios">
                                    <BloqueChips bloques={selectedInscripcion.horario?.bloques} />
                                </Descriptions.Item>
                                <Descriptions.Item label="Plan">{selectedInscripcion.plan?.nombre}</Descriptions.Item>
                                <Descriptions.Item label="Período">
                                    {dayjs(selectedInscripcion.duracion?.fechaInicio).format('DD/MM/YYYY')} – {dayjs(selectedInscripcion.duracion?.fechaTermino).format('DD/MM/YYYY')}
                                </Descriptions.Item>
                            </Descriptions>
                            <Descriptions title="Pago" column={1} bordered size="small">
                                <Descriptions.Item label="Valor plan">
                                    <Text strong style={{ color: '#0047bb' }}>${selectedInscripcion.plan?.valorFinal?.toLocaleString('es-CL')}</Text>
                                </Descriptions.Item>
                                <Descriptions.Item label="Total pagado">
                                    <Text strong style={{ color: '#0047bb', fontSize: 16 }}>${selectedInscripcion.montoTotal?.toLocaleString('es-CL')}</Text>
                                </Descriptions.Item>
                            </Descriptions>
                        </div>
                    )}
                </Modal>

                <QRAccesoModal
                    open={qrModalOpen}
                    onClose={() => setQrModalOpen(false)}
                    loading={qrLoading}
                    error={qrError}
                    data={qrData}
                    onRegenerar={handleGenerarQR}
                />
            </div>
        </div>
    );
}
